<template>
  <div class="nav-picker-container">

    <div class="reset-area">
      <transition name="fade">
        <button 
          v-if="selectedYear || selectedLocation || selectedProjectId" 
          @click="resetAll"
          class="btn-reset"
        >
          ← SHOW ALL PROJECTS
        </button>
      </transition>
    </div>

     <!-- Client Selector -->
    <div class="nav-group client-group" ref="clientBoxRef">
      <h6 class="nav-label">Client</h6>
      <div class="client-combobox">
        <input
          ref="clientInputRef"
          type="text"
          v-model="clientSearch"
          @focus="openClientDropdown"
          @input="isClientDropdownOpen = true"
          :placeholder="selectedClientName || 'Select a client...'"
          class="client-select-input"
          :class="selectedClientId ? '' : 'no-client'"
        />
        <svg class="client-chevron" :class="{ open: isClientDropdownOpen }" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>

        <Teleport to="body">
          <div
            v-if="isClientDropdownOpen"
            ref="clientDropdownRef"
            class="client-dropdown-list"
            :style="{ top: dropdownStyle.top, left: dropdownStyle.left, width: dropdownStyle.width }"
          >
            <div
              v-for="client in filteredClients"
              :key="client.id"
              class="client-option"
              :class="{ 'client-option-active': client.id === selectedClientId }"
              @mousedown.prevent="chooseClient(client)"
            >
              {{ client.name }}
            </div>
            <div v-if="filteredClients.length === 0" class="text-muted small italic client-option-empty">
              No matching clients.
            </div>
          </div>
        </Teleport>
      </div>
    </div>

    <div class="nav-group">
      <h6 class="nav-label">Year</h6>
      <div class="nav-list">
        <div v-for="year in visibleYears" :key="year"
            @click="toggleFilter('year', year)"
            :class="['nav-item', selectedYear === year ? 'active-bold' : '']">
        {{ year }}
        </div>  
        <div v-if="visibleYears.length === 0" class="text-muted small italic">
          No years with projects.
         </div>
      </div>
    </div>

    <div class="nav-group">
      <h6 class="nav-label">Location</h6>
      <div class="nav-list">
        <div v-for="loc in visibleLocations" :key="loc"
            @click="toggleFilter('location', loc)"
            :class="['nav-item', selectedLocation === loc ? 'active-bold' : '']">
        {{ loc }}
        </div>
        <div v-if="visibleLocations.length === 0" class="text-muted small italic">
          No locations.
         </div>
      </div>
    </div>

    <div class="nav-group">
      <h6 class="nav-label">Projects</h6>
      <div class="project-list">
        <div v-for="proj in visibleProjects" :key="proj.id"
            @click="selectedProjectId = proj.id"
            :class="['project-item', selectedProjectId === proj.id ? 'active-project' : '']">
        <span class="proj-name">{{ proj.year }} - {{ proj.name }}</span>
        <button @click.stop="sendToProj(proj)" class="btn btn-success small-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
        </div>
        
        <div v-if="visibleProjects.length === 0" class="text-muted small italic">
          No matching projects found.
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useGlobals } from '../../composables/globals';
import { frontendRoot } from '/src/composables/apiLink.js'

import projects from '@/composables/fe_projects.js'
const { allowedClients } = projects()
const { selectedLocation, selectedProjectId, selectedYear, selectedClientId } = useGlobals()

const clients = ref([])
const clientSearch = ref('')
const isClientDropdownOpen = ref(false)
const clientBoxRef = ref(null)
const clientInputRef = ref(null)
const clientDropdownRef = ref(null)
const dropdownStyle = ref({ top: '0px', left: '0px', width: '0px' })



onMounted(() => {
  // This logic runs once the component is added to the DOM

    allowedClients(1)
    .then(response => {
        //console.log("RESPONSE4444444a: ", response)
        if (response.length > 0){
              clients.value = response.map(item => ({
                id: item.clientId,
                name: item.clientName
              }));
        }   
    }
    ).catch(error => {
        console.log("ERROR fetching allowed clients: ", error)
        //next( {name:'select-client', query: {'goto': to.path}} )
    })
  // clients.value = [
  //   { id: 1, name: "Acme Construction Ltd." },
  //   { id: 2, name: "Prairie Infrastructure Group" },
  //   { id: 3, name: "Summit Civil Engineering" },
  //   { id: 4, name: "Foothills Contracting Inc." },
  //   { id: 5, name: "Alberta Land Developers" },
  // ];

  // Close the client dropdown when clicking anywhere outside it
  document.addEventListener('click', handleClickOutsideClientBox);
  // Keep it positioned correctly if the page scrolls or resizes
  window.addEventListener('scroll', handleWindowReposition, true);
  window.addEventListener('resize', handleWindowReposition);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutsideClientBox);
  window.removeEventListener('scroll', handleWindowReposition, true);
  window.removeEventListener('resize', handleWindowReposition);
});

// The name of the currently selected client (shown as placeholder text)
const selectedClientName = computed(() => {
  const match = clients.value.find(c => c.id === selectedClientId.value*1.0);
  return match ? match.name : '';
});

// Filters the client dropdown options based on the search box text
const filteredClients = computed(() => {
  if (!clientSearch.value.trim()) return clients.value;
  const q = clientSearch.value.toLowerCase();
  return clients.value.filter(client => client.name.toLowerCase().includes(q));
});

const openClientDropdown = () => {
  clientSearch.value = ''; // show the full list on open; typing will narrow it down
  isClientDropdownOpen.value = true;
  updateDropdownPosition();
};

const updateDropdownPosition = () => {
  if (!clientInputRef.value) return;
  const rect = clientInputRef.value.getBoundingClientRect();

  // If an ancestor (often <body> or <html>) has a CSS transform applied —
  // common in off-canvas / mobile hamburger nav implementations — it becomes
  // the containing block for position:fixed elements teleported to <body>.
  // Correct for that by measuring body's own rendered offset and subtracting it.
  const bodyRect = document.body.getBoundingClientRect();

  dropdownStyle.value = {
    top: `${rect.bottom - bodyRect.top + 4}px`,
    left: `${rect.left - bodyRect.left}px`,
    width: `${rect.width}px`,
  };
};

const chooseClient = (client) => {
  selectedClientId.value = client.id;
  clientSearch.value = '';
  isClientDropdownOpen.value = false;
};

const handleClickOutsideClientBox = (event) => {
  const clickedInsideInput = clientBoxRef.value && clientBoxRef.value.contains(event.target);
  const clickedInsideDropdown = clientDropdownRef.value && clientDropdownRef.value.contains(event.target);
  if (!clickedInsideInput && !clickedInsideDropdown) {
    isClientDropdownOpen.value = false;
  }
};

// Keep the dropdown glued to the input if the page scrolls/resizes while open
const handleWindowReposition = () => {
  if (isClientDropdownOpen.value) {
    updateDropdownPosition();
  }
};


const props = defineProps({
  allProjects: { type: Array }
})

watch(props, () => {
  console.log("HERE @@@@@@@@@@@@@@@@@@@@@@@", props.allProjects)
})

// --- COMPUTED LOGIC ---
const uniqueYears = computed(() => [...new Set(props.allProjects.map(p => p.year))].sort().reverse());
const uniqueLocations = computed(() => [...new Set(props.allProjects.map(p => p.location))].sort());
const sortedProjects = computed(() => [...new Set(props.allProjects.map(p => p.name))].sort());


const sendToProj = (proj) => {
  window.location.href = `${frontendRoot}/site/${proj.clientId}/${proj.id}`
  // console.log(`${frontendRoot}/site/${proj.clientId}/${proj.id}`)
}
// 1. Years that have projects for the selected Location
const visibleYears = computed(() => {
    // If a project is clicked, only show THAT project's year
  if (selectedProjectId.value) {
    const p = props.allProjects.find(proj => proj.id === selectedProjectId.value);
    return p ? [p.year] : [];
  }

  if (!selectedLocation.value) return uniqueYears.value;
  return uniqueYears.value.filter(year => 
    props.allProjects.some(p => p.year === year && p.location === selectedLocation.value)
  );
});

// 2. Locations that have projects for the selected Year
const visibleLocations = computed(() => {
  // If a project is clicked, only show THAT project's location
  if (selectedProjectId.value) {
    const p = props.allProjects.find(proj => proj.id === selectedProjectId.value);
    return p ? [p.location] : [];
  }
  if (!selectedYear.value) return uniqueLocations.value;
  return uniqueLocations.value.filter(loc => 
    props.allProjects.some(p => p.location === loc && p.year === selectedYear.value)
  );
});

// 3. Projects that match BOTH (The missing piece)
const visibleProjects = computed(() => {
  // NEW: If a project is clicked, hide all other projects
  if (selectedProjectId.value) {
    return props.allProjects.filter(p => p.id === selectedProjectId.value);
  }

  // Otherwise, do the normal filtering by Year/Location
  return props.allProjects.filter(p => {
    const yearMatch = !selectedYear.value || p.year === selectedYear.value;
    const locMatch = !selectedLocation.value || p.location === selectedLocation.value;
    return yearMatch && locMatch;
  });
});



// --- HELPER METHODS FOR THE "DANCE" ---
const isYearValidForLocation = (year) => {
  if (!selectedLocation.value) return true;
  return props.allProjects.some(p => p.year === year && p.location === selectedLocation.value);
};

const isLocationValidForYear = (loc) => {
  if (!selectedYear.value) return true;
  return props.allProjects.some(p => p.location === loc && p.year === selectedYear.value);
};

// --- ACTIONS ---
const toggleFilter = (type, value) => {
  selectedProjectId.value = null; // Always clear specific project on new filter
  
  if (type === 'year') {
    selectedYear.value = selectedYear.value === value ? null : value;
    selectedLocation.value = null; // Clear Location if Year is picked
  } else {
    selectedLocation.value = selectedLocation.value === value ? null : value;
    selectedYear.value = null; // Clear Year if Location is picked
  }
};

const resetAll = () => {
  selectedYear.value = null;
  selectedLocation.value = null;
  selectedProjectId.value = null;
};
</script>

<style scoped>
.btn {
  flex-shrink: 0; 
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
}

.nav-picker-container {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #333;
}

.reset-area {
  height: 40px;
  display: flex;
  align-items: center;
  width: 100%;

}

.btn-reset {
  background: none;
  border: 1px solid #ddd;
  color: var(--brand-primary);
  font-size: 0.7rem;
  font-weight: bold;
  padding: 4px 12px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-reset:hover {
  background: #f8f8f8;
  border-color: #999;
}

.nav-group {
  margin-bottom: 2rem;
  margin-left:7px;
}

.nav-label {
  font-size: medium;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgb(13, 72, 84);
  margin-bottom: 0px;
  margin-left:5px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
  font-weight: bolder;
}

.nav-item {
  cursor: pointer;
  font-size: small;
  padding: 3px 0;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.project-item {
  cursor: pointer;
  padding: 4px;
  padding-left:0px;
  margin-bottom: 3px;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  transition: all 0.3s ease;
  font-size: small;
  justify-content: space-between;
  align-items: center;
}

.proj-year {
  font-size: 0.7rem;
  background: #eee;
  padding: 2px 5px;
  border-radius: 4px;
  height: fit-content;
}

/* THE DANCE STATES */
.active-bold {
  font-weight: 800;
  color: #27ae60;
  transform: scale(1.1) translateX(12px);
}

.dimmed {
  opacity: 0.2;
}

.disabled {
  opacity: 0.05;
  pointer-events: none;
}

.active-project {
  background: rgba(39, 174, 96, 0.1);
  color: #27ae60;
  font-weight: 600;
  transform: translateX(5px);
}

.dimmed-project {
  opacity: 0.3;
  filter: grayscale(1);
}

/* Vue Animations */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }


/* Ensure the container doesn't grow past the sidebar height */
.nav-picker-container {
  display: flex;
  flex-direction: column;
  height: 100%; /* Important: fill the parent div */
  overflow: hidden; 
}

/* Make each list scrollable if it has too many items */
.nav-list, .project-list {
  overflow-y: auto;
  max-height: 175px; /* Adjust based on your UI needs */
  padding-right: 8px; /* Space for the scrollbar */
}

/* Custom "Thin" Scrollbar for a cleaner look on Chrome/Safari */
.nav-list::-webkit-scrollbar, 
.project-list::-webkit-scrollbar {
  width: 4px;
}

.nav-list::-webkit-scrollbar-thumb, 
.project-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

.nav-list::-webkit-scrollbar-thumb:hover, 
.project-list::-webkit-scrollbar-thumb:hover {
  background: #27ae60; /* Matches your S. Alberta Green */
}

.nav-list, .project-list {
  scrollbar-gutter: stable; /* Keeps the space reserved even when empty */
  overflow-y: auto;
}
/* Allow text to wrap if it's too long, or use ellipsis (...) */
.nav-item, .project-item {
    white-space: normal; /* Allows the name to go to 2 lines if needed */
    /* OR use these 3 lines if you want it to stay 1 line with "..." */
    /* overflow: hidden; */
    /* text-overflow: ellipsis; */
    /* white-space: nowrap; */
}
#sidebar {
    height: 100vh; /* Or whatever your sidebar height is */
    display: flex;
    flex-direction: column;
    overflow: hidden; 
}
.nav-list, .project-list {
    overflow-y: auto !important; /* Force the scrollbar back */
    overflow-x: hidden;         /* Prevent side-to-side shaking */
}
.proj-name {
    flex: 1;          /* takes all available space */
  min-width: 0;     /* allows ellipsis to work inside flex */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
}

.client-combobox {
  position: relative;
  width: 100%;
}

.client-select-input {
  width: 100%;
  padding: 6px 28px 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #333;
  background: white;
  cursor: text;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.no-client {
  border: 1px solid red!important;
}

.client-select-input:focus {
  border-color: #27ae60;
}

.client-select-input::placeholder {
  color: #888;
}

.client-chevron {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
  transition: transform 0.2s;
}

.client-chevron.open {
  transform: translateY(-50%) rotate(180deg);
}

.client-dropdown-list {
  position: fixed;
  max-height: 180px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  padding: 4px;
  box-sizing: border-box;
}

.client-option {
  padding: 6px 8px;
  font-size: 0.85rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}

.client-option:hover {
  background: rgba(39, 174, 96, 0.1);
  color: #27ae60;
}

.client-option-active {
  background: rgba(39, 174, 96, 0.12);
  color: #27ae60;
  font-weight: 600;
}

.client-option-empty {
  padding: 6px 8px;
}

.client-dropdown-list::-webkit-scrollbar {
  width: 4px;
}

.client-dropdown-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

.client-group {
  margin-bottom: 0px;
  padding-bottom: 30px;
  position: relative;
}
.small-btn {
  width: 12px;
  height: 12px;
  padding: 0;
}
</style>